/**
 * On Pi/TV kiosk mode: after inactivity, close modals and return to main dashboard.
 * Also supports large trackpads (pointer + keyboard + touch).
 */
import { useEffect, useRef } from 'react';
import { useAppStore } from '../stores/appStore';

const DEFAULT_IDLE_MS = 120_000;

export function useKioskIdle(enabled: boolean, idleMs = DEFAULT_IDLE_MS): void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeDay = useAppStore((s) => s.closeDay);
  const closeEventEditor = useAppStore((s) => s.closeEventEditor);
  const setLayoutEditMode = useAppStore((s) => s.setLayoutEditMode);

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        closeDay();
        closeEventEditor();
        setLayoutEditMode(false);
        // Scroll to top of dashboard
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Optional: blur any focused field
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, idleMs);
    };

    const events = [
      'pointerdown',
      'pointermove',
      'keydown',
      'wheel',
      'touchstart',
      'mousemove',
    ] as const;

    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      if (timer.current) clearTimeout(timer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [enabled, idleMs, closeDay, closeEventEditor, setLayoutEditMode]);
}
